import api from './api';
import type { SubscriptionPlan, RecruiterPack } from '@/types';

export interface RecruiterProfile {
  id: string;
  userId: string;
  companyName: string;
  companyWebsite?: string | null;
  companySize?: string | null;
  industry?: string | null;
  description?: string | null;
  contactPerson: string;
  phone?: string | null;
  address?: string | null;
  taxId?: string | null;
  isVerified: boolean;
  aiMatchesUsed: number;
  contactRevealsUsed: number;
  createdAt: string;
  updatedAt: string;
  recentPacks: RecruiterPack[];
}

export interface DashboardStats {
  savedCandidatesCount: number;
  profileViewsCount: number;
  searchHistoryCount: number;
  recentSavedCandidates: Array<{
    id: string;
    candidateId: string;
    notes: string | null;
    savedAt: string;
    candidate: {
      firstName: string;
      lastName: string;
      jobTitle: string | null;
      location: string | null;
      profileSlug: string;
      profilePhotoUrl: string | null;
    };
  }>;
  recentSearchQueries: string[];
}

export interface PackRecord {
  id: string;
  planName: string;
  aiMatchesQuota: number;
  contactRevealsQuota: number;
  pricePaidCents: number;
  purchasedAt: string;
}

export interface QuotaBalance {
  aiMatchesUsed: number;
  aiMatchesAllowed: number;
  aiMatchesRemaining: number;
  contactRevealsUsed: number;
  contactRevealsAllowed: number;
  contactRevealsRemaining: number;
  paidPacksCount: number;
}

export interface Candidate {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  profilePhotoUrl?: string | null;
  contactRevealed?: boolean;
  jobTitle?: string | null;
  location?: string | null;
  yearsExperience: number;
  employmentStatus?: 'ACTIVELY_LOOKING' | 'OPEN_TO_OFFERS' | 'NOT_LOOKING' | null;
  salaryMin?: number | null;
  salaryMax?: number | null;
  profileCompletion: number;
}

export interface SearchFilters {
  query?: string;
  jobTitle?: string;
  location?: string;
  skills?: string[];
  yearsExperienceMin?: number;
  yearsExperienceMax?: number;
  salaryMin?: number;
  salaryMax?: number;
  employmentStatus?: ('ACTIVELY_LOOKING' | 'OPEN_TO_OFFERS' | 'NOT_LOOKING')[];
  page?: number;
  limit?: number;
}

export interface SearchResponse {
  candidates: Candidate[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface FullCandidate extends Candidate {
  // Only present after contact is revealed
  firstName?: string | null;
  lastName?: string | null;
  profileSlug?: string | null;
  profilePhotoUrl?: string | null;
  // Always present
  bio?: string | null;
  cvFileUrl?: string | null;
  createdAt: string;
  workExperiences: Array<{
    id: string;
    companyName: string;
    jobTitle: string;
    location?: string | null;
    startDate: string;
    endDate?: string | null;
    isCurrent: boolean;
    description?: string | null;
  }>;
  educations: Array<{
    id: string;
    institution: string;
    degree: string;
    fieldOfStudy?: string | null;
    startDate: string;
    endDate?: string | null;
    isCurrent: boolean;
    description?: string | null;
  }>;
  skills: Array<{
    skillId: string;
    skillName: string;
    category?: string | null;
    level?: string | null;
    yearsOfExperience?: number | null;
  }>;
  languages: Array<{
    languageId: string;
    languageName: string;
    languageCode: string;
    proficiency: string;
  }>;
  contactRevealed: boolean;
}

export interface ContactInfo {
  email: string;
  phone: string | null;
}

export interface SavedCandidate {
  id: string;
  candidateId: string;
  notes: string | null;
  savedAt: string;
  source: 'MANUAL' | 'AI_MATCHING' | 'CONTACT_REVEAL';
  candidate: {
    firstName: string;
    lastName: string;
    jobTitle?: string | null;
    location?: string | null;
    yearsExperience: number;
    employmentStatus?: 'ACTIVELY_LOOKING' | 'OPEN_TO_OFFERS' | 'NOT_LOOKING' | null;
    profileSlug: string;
    profilePhotoUrl?: string | null;
    profileCompletion: number;
  };
}

const recruiterService = {
  // Get recruiter profile
  async getProfile(): Promise<RecruiterProfile> {
    const response = await api.get('/recruiters/profile');
    return response.data;
  },

  // Update recruiter profile
  async updateProfile(data: Partial<RecruiterProfile>): Promise<RecruiterProfile> {
    const response = await api.put('/recruiters/profile', data);
    return response.data;
  },

  // Get dashboard stats
  async getDashboardStats(): Promise<DashboardStats> {
    const [dashboardResponse, searchHistoryResponse] = await Promise.all([
      api.get('/recruiters/dashboard'),
      api.get('/recruiters/search-history'),
    ]);

    const dashboardStats: DashboardStats = dashboardResponse.data;
    const searchHistory: Array<{ query: string }> = searchHistoryResponse.data.searchHistory;

    // Assuming searchHistory is an array of objects with a 'query' property
    dashboardStats.recentSearchQueries = searchHistory.map(item => item.query);

    return dashboardStats;
  },

  // Get paginated pack history, optionally filtered by month/year
  async getPackHistory(params: {
    page?: number;
    limit?: number;
    month?: number;
    year?: number;
  } = {}): Promise<{ packs: PackRecord[]; pagination: { page: number; limit: number; total: number; totalPages: number } }> {
    const response = await api.get('/recruiters/packs', { params });
    return response.data;
  },

  // Get quota balance
  async getQuota(): Promise<QuotaBalance> {
    const response = await api.get('/recruiters/quota');
    return response.data;
  },

  // Search candidates
  async searchCandidates(filters: SearchFilters): Promise<SearchResponse> {
    // Convert employmentStatus array to single value for backend compatibility
    const backendFilters = {
      ...filters,
      employmentStatus: filters.employmentStatus && filters.employmentStatus.length > 0
        ? filters.employmentStatus[0]
        : undefined,
    };
    const response = await api.post('/recruiters/search', backendFilters);
    return response.data;
  },

  // View full candidate profile (costs 1 credit)
  async viewCandidate(candidateId: string): Promise<FullCandidate> {
    const response = await api.get(`/recruiters/candidate/${candidateId}`);
    return response.data;
  },

  // Reveal candidate contact
  async revealContact(candidateId: string): Promise<{ message: string; contact: ContactInfo; alreadyRevealed?: boolean }> {
    const response = await api.post(`/recruiters/reveal-contact/${candidateId}`);
    return response.data;
  },

  // Get saved candidates
  async getSavedCandidates(): Promise<{ savedCandidates: SavedCandidate[] }> {
    const response = await api.get('/recruiters/saved');
    return response.data;
  },

  // Save candidate
  async saveCandidate(candidateId: string, notes?: string): Promise<SavedCandidate> {
    const response = await api.post('/recruiters/saved', { candidateId, notes });
    return response.data;
  },

  // Remove saved candidate
  async removeSavedCandidate(savedId: string): Promise<{ message: string }> {
    const response = await api.delete(`/recruiters/saved/${savedId}`);
    return response.data;
  },

  // Update saved candidate notes
  async updateSavedCandidateNotes(savedId: string, notes: string): Promise<SavedCandidate> {
    const response = await api.put(`/recruiters/saved/${savedId}`, { notes });
    return response.data;
  },

  // Get search history
  async getSearchHistory(): Promise<{ searchHistory: Array<{ query: string; timestamp?: string }> }> {
    const response = await api.get('/recruiters/search-history');
    return response.data;
  },

  // Get available packs
  async getPacks(): Promise<SubscriptionPlan[]> {
    const response = await api.get('/pricing/packs');
    return response.data.data;
  },

  // Create checkout session (legacy Stripe)
  async createCheckoutSession(priceId: string): Promise<{ sessionId: string }> {
    const response = await api.post('/subscriptions/create-checkout', { priceId });
    return response.data;
  },

  // --- Flouci payment methods ---

  // Initiate a subscription payment; returns a Flouci payment URL
  async createSubscriptionPayment(planId: string): Promise<{
    paymentUrl: string;
    paymentId: string;
    subscriptionId: string;
  }> {
    const response = await api.post('/payments/create-subscription-payment', { planId });
    return response.data;
  },

  // Check for a pending (incomplete) payment session
  async getPendingPayment(): Promise<{
    pending: boolean;
    paymentUrl?: string;
    paymentId?: string;
    subscriptionId?: string;
    plan?: { id: string; name: string; description: string; priceCents: number };
  }> {
    const response = await api.get('/payments/pending');
    return response.data;
  },

  // Cancel the current pending payment
  async cancelPendingPayment(): Promise<{ message: string }> {
    const response = await api.post('/payments/cancel');
    return response.data;
  },

  // Poll the status of a specific payment
  async getPaymentStatus(paymentId: string): Promise<{
    paymentId: string;
    status: string;
    subscriptionStatus: string;
    completed: boolean;
  }> {
    const response = await api.get(`/payments/status/${paymentId}`);
    return response.data;
  },
};

export default recruiterService;
